import React, { useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import storage from 'local-storage-fallback';

import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select, SelectTrigger, SelectContent, SelectItem } from './ui/select';
import { LS_ONBOARDING_V1_KEY } from '../lib/constants';
import { useTracking, USER_EVENTS } from '../hooks/useTracking';
import { useUserInfo } from '@/hooks/useUserInfo';

interface EhPostSignupDialogProps {
  forceStep?: 1 | 2;  
  open: boolean;
  onClose: ({ onboardingCompleted }: { onboardingCompleted: boolean }) => void;
  user: User;
}

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'unknown', label: 'Won\'t say' }
];

const EhPostSignupDialog: React.FC<EhPostSignupDialogProps> = ({ open, onClose, user, forceStep = 1 }) => {
  const { trackEvent } = useTracking();
  const { updateUserDetails } = useUserInfo();

  const [step, setStep] = useState<1 | 2>(forceStep);
  const [isCompleted, setIsCompleted] = useState(false);
  const [nickname, setNickname] = useState('');
  const [gender, setGender] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setStep(forceStep);
  }, [forceStep]);

  const setLocalStorageOnboardingFallback = () => {
    storage.setItem(LS_ONBOARDING_V1_KEY, JSON.stringify({
      at: '0', // special value, don't ask again
      uid: user.id
    }));
  };

  // Step 1: Update user_details
  const handleSave = async () => {
    setLoading(true);
    setError('');
    
    try {
      await updateUserDetails({
        ...(nickname && { usr_nickname: nickname }),
        ...(gender && { usr_gender: gender }),
        eh_usr_meta: {
          hasSetDetailsV1At: new Date().toISOString(),
        },
      });
      setStep(2);
    } catch (err) {
      if (import.meta.env.DEV) {
        setError(`[DEV] Failed to save user details: ${err instanceof Error ? err.message : 'Unknown error'}`);
        console.error('[DEV] handleSave:', err);
      } else {
        setError('Sorry, something went wrong. Please retry later.');
      }
      trackEvent(USER_EVENTS.USER_ONBOARDING_ERROR, {
        error: `Failed to save user details: ${err instanceof Error ? err.message : 'Unknown error'}`,
        step: '1: user_details',
      });
      setLocalStorageOnboardingFallback();
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Confirm 18+ and onboarding
  const handleConfirmAge = async (is18Plus?: boolean) => {
    setLoading(true);
    setError('');
    
    try {
      const now = new Date().toISOString();
      // Update user_details.eh_usr_meta
      await updateUserDetails({
        eh_usr_meta: {
          ...(is18Plus ? { age18ConfirmedAt: now } : { notAge18DisclosedAt: now }),
          onboardingV1completedAt: now,
        },
      });
      setIsCompleted(true);
      onClose({ onboardingCompleted: true });
    } catch (err) {
      if (import.meta.env.DEV) {
        setError(`[DEV] Failed to confirm onboarding: ${err instanceof Error ? err.message : 'Unknown error'}`);
        console.error('[DEV] handleConfirmAge:', err);
      } else {
        setError('Sorry, something went wrong.');
      }
      trackEvent(USER_EVENTS.USER_ONBOARDING_ERROR, {
        error: `Failed to confirm onboarding: ${err instanceof Error ? err.message : 'Unknown error'}`,
        step: '2: confirm_18',
      });
      setLocalStorageOnboardingFallback();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose({ onboardingCompleted: isCompleted })}>
      <DialogContent className="max-w-md bg-zinc-900 border border-zinc-700 text-white">
        {step === 1 && (
          <div className="flex flex-col gap-4 p-2">
            <DialogTitle className="text-xl font-bold text-white mb-1">
              Personalize Your AI Experience
              </DialogTitle>
            <DialogDescription className="text-white/80 text-base mb-2">
              It will help to customize your interactions with the AI characters.
            </DialogDescription>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">{/* user icon */}
                <svg width="18" height="18" fill="currentColor"><path d="M9 9a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0 2c-3.31 0-6 1.34-6 3v2a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-2c0-1.66-2.69-3-6-3z"/></svg>
              </span>
              <Input
                className="pl-9 bg-zinc-800 border-none text-white placeholder-white/70"
                placeholder="Nickname"
                value={nickname}
                onChange={e => setNickname(e.target.value)}
                disabled={loading}
              />
            </div>
            <Select value={gender} onValueChange={setGender} disabled={loading}>
              <SelectTrigger className="w-full bg-zinc-800 border-none text-white px-3 py-2 rounded mt-1 mb-2">
                {GENDER_OPTIONS.find(opt => opt.value === gender)?.label ?? 'Select gender'}
              </SelectTrigger>
              <SelectContent>
                {GENDER_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              className="w-full bg-pink-700 hover:bg-pink-600 text-white font-bold text-base py-3 mt-2"
              onClick={handleSave}
              disabled={loading}
            >
              <span className="mr-2">{/* save icon */}
                <svg width="18" height="18" fill="currentColor"><path d="M17 3a2 2 0 0 0-2-2H3a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3zm-2 0v2H3V3h12zM3 17v-7h12v7H3z"/></svg>
              </span>
              <span>Save</span>
            </Button>
            {error && <div className="text-red-500 text-sm mt-2">{error}</div>}
          </div>
        )}
        {step === 2 && (
          <div className="flex flex-col p-2">
            <h2 className="text-2xl font-bold text-pink-600 mb-1 text-center">Content Warning</h2>
            <div className="text-lg font-bold text-white text-center mb-1">AI-generated content may be explicit.</div>
            <div className="text-base font-semibold text-zinc-300 text-center mb-2">Please confirm that you are a legal adult to enable restricted features.</div>
            <div className="text-white/80 text-sm mb- text-justify mt-2 mb-4">
              <p>
                Providing inaccurate information about your age constitutes a violation of our Terms.
                By using the site, you agree to our Terms of Service and Policies. Our privacy notice details how we collect and use your data.
                By continuing to use this site, you consent to receive email updates from us exclusively, which you may opt out of at any time in account settings.
              </p>
              <br />
              <p>
                All content on this website is AI-generated!
                Any generations that resemble real people are purely coincidental or prompted by users.
                We do not guarantee the accuracy of the content. Please report any inappropriate content as per our policies.
              </p>
            </div>
            <div className="flex gap-2 justify-center mb-2">
              <a href="/legal/terms-of-service" target="_blank" rel="noopener noreferrer">
                <Button variant="ghost">Terms of Service</Button>
              </a>
              <a href="/legal/privacy-policy" target="_blank" rel="noopener noreferrer">
                <Button variant="ghost">Privacy Notice</Button>
              </a>
            </div>
            <Button
              className="w-full bg-pink-700 hover:bg-pink-600 text-white font-bold text-base py-3 mt-2 mb-2"
              onClick={() => handleConfirmAge(true)}
              disabled={loading}
            >
              I am over 18
            </Button>
            <Button
              className="w-full text-white font-bold text-base py-3 mt-2 border border-zinc-700"
              variant="ghost"
              onClick={() => handleConfirmAge(false)}
              disabled={loading}
            >
              I am under 18 or a minor
            </Button>
            {error && <div className="text-red-500 text-sm mt-2">{error}</div>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EhPostSignupDialog;
